import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppBarComponent from '../components/AppBarComponent';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../apiConfig';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';

const BRAND = '#facc15';
const BRAND_RGB = '250,204,21';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('pt-BR');
};

export default function Documentos() {
  const { getAuthHeaders } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // file object
  const [actionLoading, setActionLoading] = useState(null);
  const abortRef = useRef(null); // Bug 15: cancelar fetch anterior ao re-fetch

  const fetchFiles = useCallback(async () => {
    // Cancela fetch anterior se ainda em andamento
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(apiUrl('/api/files'), {
        headers: getAuthHeaders(), // Bug 11: JWT correto
        signal: controller.signal,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao carregar arquivos');
      }
      const data = await response.json();
      setFiles(data);
    } catch (err) {
      if (err.name === 'AbortError') return; // fetch cancelado — não atualiza estado
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchFiles();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchFiles]);

  const handleDownload = async (file) => {
    setActionLoading(file.filename);
    try {
      const response = await fetch(apiUrl(`/api/download/${file.filename}`), {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Erro ao fazer download');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalName || file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (file) => {
    setDeletingId(file.filename);
    try {
      const response = await fetch(apiUrl(`/api/files/${file.filename}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao deletar arquivo');
      }
      setFiles(prev => prev.filter(f => f.filename !== file.filename));
      setConfirmDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#020617', color:'#f1f5f9', fontFamily:'Inter, system-ui, sans-serif' }}>
      <style>{`
        * { box-sizing: border-box; }
        .doc-row { transition: background 0.15s; }
        .doc-row:hover { background: rgba(255,255,255,0.03); }
        .btn-icon { background: transparent; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 7px 10px; cursor: pointer; transition: all 0.2s; color: rgba(241,245,249,0.6); font-size: 0.85rem; }
        .btn-icon:hover:not(:disabled) { background: rgba(255,255,255,0.08); color: #f1f5f9; }
        .btn-icon.danger:hover:not(:disabled) { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.3); color: #fca5a5; }
        .btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 24px; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <AppBarComponent />

      <div style={{ maxWidth:960, margin:'0 auto', padding:'40px 24px' }}>
        <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4 }}>

          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
            <div>
              <span style={{ display:'inline-block', background:`rgba(${BRAND_RGB},0.08)`, border:`1px solid rgba(${BRAND_RGB},0.25)`, borderRadius:100, padding:'4px 14px', fontSize:'0.7rem', fontWeight:700, color:BRAND, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>
                Documentos
              </span>
              <h1 style={{ fontSize:'1.6rem', fontWeight:900, margin:0 }}>Seus arquivos CSV</h1>
            </div>
            <button
              onClick={fetchFiles}
              disabled={loading}
              style={{ background:`rgba(${BRAND_RGB},0.1)`, border:`1px solid rgba(${BRAND_RGB},0.25)`, borderRadius:12, padding:'10px 20px', color:BRAND, fontWeight:700, fontSize:'0.88rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, transition:'all 0.2s' }}>
              {loading ? '...' : '↻ Atualizar'}
            </button>
          </div>

          {error && (
            <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.28)', borderRadius:12, padding:'12px 16px', marginBottom:20, fontSize:'0.875rem', color:'#fca5a5', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>⚠ {error}</span>
              <button onClick={() => setError(null)} style={{ background:'transparent', border:'none', color:'#fca5a5', cursor:'pointer', fontSize:'1rem', padding:0 }}>✕</button>
            </div>
          )}

          <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:20, overflow:'hidden' }}>
            {loading ? (
              <div style={{ padding:'60px 24px', textAlign:'center' }}>
                <div style={{ width:36, height:36, border:'3px solid rgba(255,255,255,0.1)', borderTopColor:BRAND, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 14px' }}/>
                <div style={{ color:'rgba(241,245,249,0.4)', fontSize:'0.9rem' }}>Carregando arquivos...</div>
              </div>
            ) : files.length === 0 ? (
              <div style={{ padding:'60px 24px', textAlign:'center' }}>
                <div style={{ fontSize:'3rem', marginBottom:12 }}>📁</div>
                <div style={{ fontWeight:600, color:'rgba(241,245,249,0.5)', marginBottom:6 }}>Nenhum arquivo encontrado</div>
                <div style={{ fontSize:'0.82rem', color:'rgba(241,245,249,0.3)' }}>Os arquivos CSV enviados aparecerão aqui</div>
              </div>
            ) : (
              <>
                {/* Cabeçalho da tabela */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 100px 160px 100px', gap:16, padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', fontSize:'0.72rem', fontWeight:700, color:'rgba(241,245,249,0.3)', textTransform:'uppercase', letterSpacing:'0.07em' }}>
                  <span>Arquivo</span>
                  <span>Tamanho</span>
                  <span>Enviado em</span>
                  <span style={{ textAlign:'center' }}>Ações</span>
                </div>
                {files.map((file, i) => (
                  <div key={file.filename} className="doc-row" style={{ display:'grid', gridTemplateColumns:'1fr 100px 160px 100px', gap:16, padding:'14px 20px', borderBottom: i < files.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                      <div style={{ width:34, height:34, borderRadius:9, background:'rgba(250,204,21,0.08)', border:'1px solid rgba(250,204,21,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.9rem', flexShrink:0 }}>📄</div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:'0.88rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{file.originalName || file.filename}</div>
                        <div style={{ fontSize:'0.72rem', color:'rgba(241,245,249,0.3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2 }}>{file.filename}</div>
                      </div>
                    </div>
                    <div style={{ fontSize:'0.82rem', color:'rgba(241,245,249,0.5)' }}>{formatFileSize(file.size)}</div>
                    <div style={{ fontSize:'0.82rem', color:'rgba(241,245,249,0.5)' }}>{formatDate(file.uploadDate)}</div>
                    <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                      <button className="btn-icon" title="Baixar" onClick={() => handleDownload(file)} disabled={actionLoading === file.filename || deletingId === file.filename}>
                        {actionLoading === file.filename
                          ? <span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.2)', borderTopColor:'#f1f5f9', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}/>
                          : '⬇'}
                      </button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => setConfirmDelete(file)} disabled={actionLoading === file.filename || deletingId === file.filename}>
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.05)', fontSize:'0.78rem', color:'rgba(241,245,249,0.3)', textAlign:'right' }}>
                  {files.length} arquivo{files.length !== 1 ? 's' : ''}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} transition={{ duration:0.2 }}
            onClick={e => e.stopPropagation()}
            style={{ background:'#0f172a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:18, padding:28, maxWidth:440, width:'100%' }}>
            <div style={{ fontSize:'1.5rem', marginBottom:12 }}>🗑</div>
            <h3 style={{ fontWeight:800, fontSize:'1.05rem', marginBottom:8 }}>Confirmar exclusão</h3>
            <p style={{ color:'rgba(241,245,249,0.55)', fontSize:'0.88rem', marginBottom:6 }}>
              Tem certeza que deseja excluir o arquivo:
            </p>
            <p style={{ color:BRAND, fontWeight:600, fontSize:'0.9rem', wordBreak:'break-all', marginBottom:20 }}>
              "{confirmDelete.originalName || confirmDelete.filename}"
            </p>
            <p style={{ fontSize:'0.78rem', color:'rgba(241,245,249,0.3)', marginBottom:24 }}>Esta ação não pode ser desfeita.</p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} disabled={!!deletingId}
                style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'10px 20px', color:'rgba(241,245,249,0.7)', fontWeight:600, cursor:'pointer', fontSize:'0.88rem' }}>
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={!!deletingId}
                style={{ background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:10, padding:'10px 20px', color:'#fca5a5', fontWeight:700, cursor: deletingId ? 'not-allowed' : 'pointer', fontSize:'0.88rem', opacity: deletingId ? 0.6 : 1, display:'flex', alignItems:'center', gap:8 }}>
                {deletingId ? <span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.2)', borderTopColor:'#fca5a5', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}/> : null}
                Excluir
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
